// @flow

import * as React from "react";
import { withRouter, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import NProgress from "nprogress";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";

import API from "../api/user";
import Config from "../constants/Config";
import scrollToEl from "../utils/scrollToEl";
import { customValidators } from "../utils/validators";
import { getEventDateString, parseDateString } from "../utils/date";
import convertEmptyKeyToNull from "../utils/convertEmptyKeyToNull";
import useAsyncState from "../hooks/useAsyncState";
import Loader from "../components/atoms/Loader";
import HeroProfilePage from "../components/organisms/HeroProfilePage";
import CustomMeta from "../components/atoms/CustomMeta.js";
import StatusBlock from "../components/atoms/StatusBlock";
import EditableQuestionAccordions from "../components/organisms/EditableQuestionAccordions";
import EditableMediaAccordions from "../components/organisms/EditableMediaAccordions";
import Button from "../components/atoms/Button";
import ReactFormFiedRTE from "../components/atoms/ReactFormFieldRTE";
import ServerErrorMessage from "../components/atoms/ServerErrorMessage";
import NotFound from "../containers/NotFound";
import Pill from "../components/atoms/Pill";
import checkIfTicketForSale from "../utils/checkIfTicketForSale";
import checkIfTicketInFuture from "../utils/checkIfTicketInFuture.js";
import TicketAccordionsForm from "../components/organisms/TicketAccordionsForm";
type PropsType = {
    match: any,
};

const EditEvent = (props: PropsType): React.Node => {
    const [state, localActions] = useAsyncState();
    const [pageError, setPageError] = React.useState(null);
    const [activeEvent, setActiveEvent] = React.useState(null);
    const [revisionEvent, setRevisionEvent] = React.useState(null);
    const [eventStatus, setEventStatus] = React.useState(null);
    const [eventStatusReason, setEventStatusReason] = React.useState(null);
    // const [eventForm, setEventForm] = React.useState(null);
    const [eventMedia, setEventMedia] = React.useState(null);
    const [eventFaqs, setEventFaqs] = React.useState(null);
    const [eventProducts, setEventProducts] = React.useState(null);
    const [eventMediaValid, setEventMediaValid] = React.useState(false);
    const [eventFaqsValid, setEventFaqsValid] = React.useState(true);
    const [eventProductsValid, setEventProductsValid] = React.useState(true);
    const editableMediaRef = React.useRef({});
    const editableFaqsRef = React.useRef({});
    const editableProductsRef = React.useRef({});
    const statusRef = React.useRef(null);

    const { profile } = useSelector((state: any): any => state.user);
    const isClubExec = profile.role
        ? profile.role.filter(role => role === "club_exec").length > 0
        : false;
    const editingLocked = eventStatus === "in-review";

    async function loadPageData(startProgress = true) {
        if (!isClubExec) {
            <Redirect to={Config.routes.login} />;
        }

        if (props.match.params.id) {
            NProgress.start();
            localActions.request();
            setPageError(null);

            await Promise.all([
                // event detail
                API.userGetManagedEvent(props.match.params.id).then(
                    (data: any) => {
                        if (data.revision) {
                            // set base event revision data
                            setRevisionEvent(data.revision);
                        }

                        if (data.event) {
                            // set base event data
                            setActiveEvent(data.event);
                        }

                        // set data for media/faqs based off available revision data
                        const editableData = data.revision || data.event;

                        setEventFaqs(
                            editableData.acf.faqs &&
                                editableData.acf.faqs.sections
                                ? editableData.acf.faqs.sections.map(
                                      faqSection => {
                                          return {
                                              title: faqSection.title,
                                              content: faqSection.content,
                                          };
                                      }
                                  )
                                : []
                        );

                        setEventProducts(
                            editableData.linked_products &&
                                editableData.linked_products
                                ? editableData.linked_products.map(
                                      linkedProducts => {
                                          return {
                                              id: linkedProducts.product_id,
                                              price: linkedProducts.price,
                                              qty: linkedProducts.current_stock_quantity,
                                              available_from:
                                                  linkedProducts.available_from,
                                              available_until:
                                                  linkedProducts.available_to,
                                          };
                                      }
                                  )
                                : []
                        );

                        setEventMedia(
                            editableData.acf.hero && editableData.acf.hero.media
                                ? editableData.acf.hero.media.map(
                                      heroMediaItem => {
                                          return {
                                              type: heroMediaItem.youtube_url
                                                  ? "video"
                                                  : "image",
                                              image_id: heroMediaItem.image
                                                  ? heroMediaItem.image.ID
                                                  : null,
                                              url: heroMediaItem.youtube_url
                                                  ? heroMediaItem.youtube_url
                                                        .url
                                                  : null,
                                              caption: heroMediaItem.caption,
                                              alt:
                                                  heroMediaItem.caption ||
                                                  heroMediaItem.image
                                                      ? heroMediaItem.image.alt
                                                      : null,
                                              preview: heroMediaItem.image
                                                  ? heroMediaItem.image.sizes
                                                        .thumbnail
                                                  : null,
                                              fileName: heroMediaItem.image
                                                  ? heroMediaItem.image.filename
                                                  : null,
                                          };
                                      }
                                  )
                                : []
                        );

                        setEventStatus(data.review_status);
                        setEventStatusReason(data.reason);
                    }
                ),
            ])
                .then(() => {
                    // success
                    localActions.success();
                })
                .catch(error => {
                    // errors
                    // localActions.fail(error.message);
                    setPageError(error);
                })
                .finally(() => {
                    NProgress.done();
                });
        } else {
            setPageError({});
            // localActions.fail("Could not find a matching managed event.");
        }
    }

    // initial mount use effect
    React.useEffect((): any => {
        loadPageData();
    }, []); // eslint-disable-line

    const handleMediaUpdate = (content, isValid) => {
        setEventMediaValid(isValid);
        setEventMedia(content);
    };

    const handleFaqUpdate = (content, isValid) => {
        setEventFaqsValid(isValid);
        setEventFaqs(content);
    };

    const handleProductUpdate = (content, isValid) => {
        setEventProductsValid(isValid);
        setEventProducts(content);
    };

    const handleEventImmediateSubmit = fields => {
        // trigger validation on inner media/faqs forms
        if (editableFaqsRef.current) {
            editableFaqsRef.current.validateForm();
        }
        if (editableProductsRef.current) {
            editableProductsRef.current.validateForm();
        }

        if (eventFaqsValid && eventProductsValid) {
            // all form data is valid

            NProgress.start();
            localActions.request(
                <>
                    <p>Please wait while we process your submission.</p>
                    <p>Do not refresh the page.</p>
                </>
            );

            const updatedEventData = {
                ID: Number(props.match.params.id),
                description: fields.description,
                faqs: eventFaqs,
                products: eventProducts,
            };

            // update the event
            return API.userUpdateImmediateManagedEvent(
                Number(props.match.params.id),
                convertEmptyKeyToNull(updatedEventData)
            )
                .then(async response => {
                    localActions.success();

                    await loadPageData(false);

                    if (response.status) {
                        scrollToEl(statusRef.current, 500, -100);
                    }

                    setEventStatusReason(response.reason || null);
                })
                .catch(error => {
                    localActions.fail(
                        error.message ||
                            "There was an issue updating this event."
                    );
                })
                .finally(() => {
                    NProgress.done();
                });
        }
    };

    const handleEditEventSubmitForReview = fields => {
        // trigger validation on inner media/faqs forms
        if (editableMediaRef.current) {
            editableMediaRef.current.validateForm();
        }
        if (eventMediaValid) {
            // all form data is valid

            NProgress.start();
            localActions.request(
                <>
                    <p>Please wait while we process your submission.</p>
                    <p>Do not refresh the page.</p>
                </>
            );

            // upload image media
            uploadImageMedia()
                .then(mediaWithUploadIds => {
                    const updatedEventData = {
                        ID: Number(props.match.params.id),
                        hero_media: mediaWithUploadIds,
                        location_google_map_link:
                            fields.location_google_map_link,
                    };

                    // update the event
                    return API.userUpdateManagedEvent(
                        Number(props.match.params.id),
                        convertEmptyKeyToNull(updatedEventData)
                    );
                })
                .then(async response => {
                    localActions.success();

                    await loadPageData(false);

                    if (response.status) {
                        setEventStatus(response.status);
                        scrollToEl(statusRef.current, 500, -100);
                    }

                    setEventStatusReason(response.reason || null);
                })
                .catch(error => {
                    localActions.fail(
                        error.message ||
                            "There was an issue updating this event."
                    );
                })
                .finally(() => {
                    NProgress.done();
                });
        }
    };

    const uploadImageMedia = () => {
        const imageUploadPromises = [];

        eventMedia.forEach(mediaItem => {
            if (mediaItem.upload_file) {
                const imageUploadForm = new FormData();
                imageUploadForm.append("media_index", "0");
                imageUploadForm.append("media_file", mediaItem.upload_file);

                imageUploadPromises.push(
                    API.userUploadManagedEventMedia(
                        props.match.params.id,
                        imageUploadForm
                    ).then(response => {
                        if (response.media_id) {
                            // set uploaded media image id
                            mediaItem.image_id = response.media_id;
                        }
                    })
                );
            }
        });

        return Promise.all(imageUploadPromises).then(() => {
            const eventMediaWithUploadIds = [];

            eventMedia.forEach(mediaItem => {
                eventMediaWithUploadIds.push(
                    convertEmptyKeyToNull({
                        type: mediaItem.type,
                        image_id: mediaItem.image_id,
                        url: mediaItem.url,
                        caption: mediaItem.caption,
                        alt: mediaItem.alt,
                    })
                );
            });

            return eventMediaWithUploadIds;
        });
    };

    if (pageError) {
        return <NotFound {...pageError} />;
    }

    const renderTicketList = activeEvent => {
        return (
            <ul>
                {activeEvent.acf.event_products.map((eventProduct, key) => {
                    const ticket = activeEvent.related[eventProduct.product.ID];
                    const ticketNameMeta = ticket.meta_data.find(
                        data => data.key === "crm_ticket_name"
                    );
                    const ticketName = ticketNameMeta
                        ? ticketNameMeta.value
                        : ticket.type;
                    return ticket.type ? (
                        <li key={key}>
                            {ticketName}: ${ticket.price}
                            {!checkIfTicketForSale(ticket, props.status) &&
                            !checkIfTicketInFuture(ticket) ? (
                                <Pill color="outline">Sold out</Pill>
                            ) : (
                                ""
                            )}
                        </li>
                    ) : null;
                })}
            </ul>
        );
    };

    return (
        <React.Fragment>
            <CustomMeta title="Edit Event" />

            <HeroProfilePage
                heading="Edit Event"
                backLink={{
                    title: "Back to manage event",
                    url: `${Config.routes.manageEvent}/${props.match.params.id}`,
                }}
            />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-xs-12 col-md-8 manage-clubs__container">
                        <div className="row manage-clubs__content">
                            <div className="col-xs-12">
                                <Loader
                                    fixed={true}
                                    active={state.loading}
                                    text={state.loadingMessage}
                                />

                                {activeEvent && eventMedia && eventFaqs ? (
                                    <>
                                        {/* Event status */}
                                        <div ref={statusRef}></div>
                                        {eventStatus === "in-review" ? (
                                            <StatusBlock
                                                status="pending"
                                                heading="Your changes are awaiting review by your Clubs Coordinator."
                                                content={
                                                    <>
                                                        Further edits are
                                                        disabled until your
                                                        submitted changes have
                                                        been reviewed.
                                                        <br />
                                                        <br />
                                                        Note: only the club who
                                                        submitted the original
                                                        event application should
                                                        submit changes to the
                                                        event. If you are from a
                                                        club that is co-hosting
                                                        this event, please
                                                        liaise with the club
                                                        that submitted the
                                                        application for this
                                                        event and ask them to
                                                        submit the changes your
                                                        require.
                                                    </>
                                                }
                                            />
                                        ) : null}
                                        {eventStatus === "draft" ? (
                                            <StatusBlock
                                                status="error"
                                                heading="Your changes were rejected by the event coordinator."
                                                content={eventStatusReason}
                                            />
                                        ) : null}

                                        <h2 className="heading-2">
                                            {activeEvent.post_title}
                                        </h2>

                                        {/* Details form */}
                                        <div className="panel">
                                            <div className="panel__section">
                                                <h3 className="heading-3">
                                                    Event details
                                                </h3>

                                                <p>
                                                    Key event information that
                                                    your club submitted is shown
                                                    here. If you need to make
                                                    changes, please contact the
                                                    clubs team at{" "}
                                                    <a
                                                        href="mailto:clubs@activateuts.com.au"
                                                        className="link"
                                                    >
                                                        clubs@activateuts.com.au
                                                    </a>
                                                </p>
                                            </div>

                                            {activeEvent.acf.start_date &&
                                            activeEvent.acf.end_date ? (
                                                <div className="panel__section">
                                                    <div className="panel__heading">
                                                        Date & time
                                                    </div>
                                                    <div>
                                                        {getEventDateString(
                                                            activeEvent.acf
                                                                .start_date,
                                                            activeEvent.acf
                                                                .end_date
                                                        ).map((date, key) => (
                                                            <span key={key}>
                                                                {date}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : null}

                                            {activeEvent.event_location ? (
                                                <div className="panel__section">
                                                    <div className="panel__heading">
                                                        Location
                                                    </div>
                                                    <div>
                                                        {
                                                            activeEvent.event_location
                                                        }
                                                    </div>
                                                </div>
                                            ) : null}

                                            {activeEvent.acf.hero ? (
                                                <div className="panel__section">
                                                    <div className="panel__heading">
                                                        Short description
                                                    </div>
                                                    <div>
                                                        {
                                                            activeEvent.acf.hero
                                                                .description
                                                        }
                                                    </div>
                                                </div>
                                            ) : null}

                                            {activeEvent.acf.event_products ? (
                                                <div className="panel__section">
                                                    <div className="panel__heading">
                                                        Tickets
                                                    </div>
                                                    <div className="edit-event-tickets">
                                                        {renderTicketList(
                                                            activeEvent
                                                        )}
                                                    </div>
                                                </div>
                                            ) : null}

                                            {activeEvent.acf.hosted_by ? (
                                                <div className="panel__section">
                                                    <div className="panel__heading">
                                                        Host
                                                    </div>
                                                    <div>
                                                        <ul>
                                                            {activeEvent.acf.hosted_by.map(
                                                                (host, key) => {
                                                                    return (
                                                                        <li
                                                                            key={
                                                                                key
                                                                            }
                                                                        >
                                                                            {
                                                                                host.post_title
                                                                            }
                                                                        </li>
                                                                    );
                                                                }
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ) : null}

                                            {activeEvent.categories &&
                                            activeEvent.categories.length ? (
                                                <div className="panel__section">
                                                    <div className="panel__heading">
                                                        Event category
                                                    </div>
                                                    <div>
                                                        <ul>
                                                            {activeEvent.categories.map(
                                                                (
                                                                    category,
                                                                    key
                                                                ) => {
                                                                    return (
                                                                        <li
                                                                            key={
                                                                                key
                                                                            }
                                                                        >
                                                                            {
                                                                                category.name
                                                                            }
                                                                        </li>
                                                                    );
                                                                }
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ) : null}

                                            {activeEvent.acf
                                                .event_ticket_comment_label ? (
                                                <div className="panel__section">
                                                    <div className="panel__heading">
                                                        Event registration
                                                        question
                                                    </div>
                                                    <div>
                                                        {
                                                            activeEvent.acf
                                                                .event_ticket_comment_label
                                                        }
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>

                                        <ReactForm
                                            validators={customValidators}
                                            onSuccess={
                                                handleEventImmediateSubmit
                                            }
                                            // onUpdate={handleEventFormUpdate}
                                        >
                                            {/* Description */}
                                            <div className="panel">
                                                <div className="panel__section">
                                                    <h3 className="heading-3">
                                                        Event details you can
                                                        update any time
                                                    </h3>

                                                    <p>
                                                        Please allow up to 30
                                                        minutes to view updates
                                                        on the website, please
                                                        consider this when
                                                        making changes.
                                                    </p>

                                                    <h5 className="heading-5">
                                                        Event description
                                                    </h5>

                                                    <p>
                                                        Provide a description of
                                                        what people can expect
                                                        at your event. Why
                                                        should they come? What
                                                        will they love? Please
                                                        refrain from using
                                                        Emojis and multiple
                                                        fonts for accessibility
                                                        reasons.
                                                    </p>

                                                    <ReactFormField
                                                        fieldComponent={
                                                            ReactFormFiedRTE
                                                        }
                                                        type="text"
                                                        name="description"
                                                        defaultValue={
                                                            revisionEvent
                                                                ? revisionEvent
                                                                      .acf
                                                                      .description
                                                                    ? revisionEvent
                                                                          .acf
                                                                          .description
                                                                          .html
                                                                    : ""
                                                                : activeEvent
                                                                      .acf
                                                                      .description
                                                                ? activeEvent
                                                                      .acf
                                                                      .description
                                                                      .html
                                                                : ""
                                                        }
                                                        required={true}
                                                    />
                                                </div>

                                                {/* FAQs */}
                                                <div>
                                                    <h3 className="heading-3">
                                                        FAQs
                                                    </h3>

                                                    <p>
                                                        Provide the answers to a
                                                        few common questions
                                                        people might have about
                                                        your event.
                                                    </p>

                                                    <EditableQuestionAccordions
                                                        customRef={
                                                            editableFaqsRef
                                                        }
                                                        defaultValue={eventFaqs}
                                                        onUpdate={
                                                            handleFaqUpdate
                                                        }
                                                    />
                                                </div>

                                                <br />

                                                {/* Ticketing */}

                                                <h5 className="heading-5">
                                                    Ticketing
                                                </h5>

                                                <p>
                                                    If you require additional
                                                    changes to ticketing e.g.
                                                    creating a new ticket type.
                                                    Please contact the clubs
                                                    team via email -{" "}
                                                    <a
                                                        href="mailto:clubs@activateuts.com.au"
                                                        className="link"
                                                    >
                                                        clubs@activateuts.com.au
                                                    </a>
                                                </p>

                                                <TicketAccordionsForm
                                                    customRef={
                                                        editableProductsRef
                                                    }
                                                    products={
                                                        activeEvent?.linked_products
                                                    }
                                                    onUpdate={
                                                        handleProductUpdate
                                                    }
                                                />

                                                <ServerErrorMessage>
                                                    {state.error}
                                                </ServerErrorMessage>

                                                <br />
                                                <Button
                                                    inline={true}
                                                    styles={["green"]}
                                                    loading={state.loading}
                                                    success={!!state.success}
                                                >
                                                    Submit Changes
                                                </Button>

                                                <p>
                                                    Your changes will not be
                                                    visible until the event is
                                                    published (live). Changes
                                                    made to a live event will be
                                                    visible on your event page
                                                    within 30 minutes. If you
                                                    wish to make additional
                                                    changes please resubmit.
                                                </p>
                                            </div>
                                        </ReactForm>
                                        <ReactForm
                                            validators={customValidators}
                                            onSuccess={
                                                handleEditEventSubmitForReview
                                            }
                                            // onUpdate={handleEventFormUpdate}
                                        >
                                            <div className="panel">
                                                <div className="panel__section">
                                                    <h3 className="heading-3">
                                                        Event details that
                                                        require approval
                                                    </h3>

                                                    <p>
                                                        The following changes
                                                        require approval from
                                                        ActivateUTS. These
                                                        changes may take 3-5
                                                        Business Days for
                                                        changes to be visible on
                                                        the event page.
                                                    </p>

                                                    <br />

                                                    {/* Gallery */}
                                                    <h5 className="heading-5">
                                                        Event gallery
                                                    </h5>

                                                    <p>
                                                        Please note: Captions
                                                        will accompany the image
                                                        in the gallery. Alt text
                                                        will be hidden but will
                                                        be used to assist users
                                                        with screen readers.
                                                    </p>

                                                    <EditableMediaAccordions
                                                        customRef={
                                                            editableMediaRef
                                                        }
                                                        defaultValue={
                                                            eventMedia
                                                        }
                                                        onUpdate={
                                                            handleMediaUpdate
                                                        }
                                                        maxItems={5}
                                                        disabled={editingLocked}
                                                    />

                                                    <br />

                                                    {/* Google maps link */}
                                                    <div
                                                        className={`${
                                                            revisionEvent &&
                                                            revisionEvent.acf
                                                                .location_google_map_link
                                                                ? "has-value"
                                                                : ""
                                                        }`}
                                                    >
                                                        <div>
                                                            <h5 className="heading-5">
                                                                Google maps link
                                                            </h5>
                                                            <div>
                                                                Look up your
                                                                event venue or
                                                                location on
                                                                Google Maps,
                                                                then paste the
                                                                URL below. This
                                                                will embed a map
                                                                of the location
                                                                on your event
                                                                page.
                                                            </div>
                                                            <ReactFormField
                                                                type="text"
                                                                name="location_google_map_link"
                                                                label="Google maps URL"
                                                                placeholder=""
                                                                defaultValue={
                                                                    revisionEvent
                                                                        ? revisionEvent
                                                                              .acf
                                                                              .location_google_map_link
                                                                        : activeEvent
                                                                              .acf
                                                                              .location_google_map_link
                                                                }
                                                                validators={[
                                                                    "url",
                                                                ]}
                                                                disabled={
                                                                    editingLocked
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <p>
                                                    Your event will be submitted
                                                    to your club coordinator for
                                                    approval.
                                                </p>

                                                <ServerErrorMessage>
                                                    {state.error}
                                                </ServerErrorMessage>

                                                <Button
                                                    inline={true}
                                                    styles={["green"]}
                                                    disabled={editingLocked}
                                                    loading={state.loading}
                                                    success={!!state.success}
                                                >
                                                    Submit changes for review
                                                </Button>
                                            </div>
                                        </ReactForm>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

EditEvent.preload = ({ location, passed: { store } }: any): Promise<any> => {
    return Promise.all([]);
};

export default withRouter(EditEvent);
